'use client';

import { CheckoutCateringFields } from './CheckoutCateringFieldset';
import CheckoutExtraPageForm from './CheckoutExtraPageForm';
import { api } from '~/modules';
import { graphql } from '@funkey/types/graphql/gql';
import { processGraphqlFormErrors } from '~/utils/errors';
import useCartState from '~/features/cart/useCartState';
import { useFormState, useValidateWithFeedback } from '~/modules/form';
import { useI18n } from '~/features/i18n';
import useNotifications from '~/features/notifications/useNotifications';
import { usePortalRouter } from '~/features/global/portal';
import { z } from 'zod';
import { CheckoutPageTemplate } from '../CheckoutPageTemplate';
import { useMemo } from 'react';
import { redirect } from 'next/navigation';
import { PORTAL_RELATIVE_HREF_CART, PORTAL_RELATIVE_HREF_CHECKOUT } from '../../hooks';
export default function CheckoutExtraPageWithState() {
  const i18n = useI18n();
  const router = usePortalRouter();
  const notifications = useNotifications();
  const cart = useCartState();
  const cartQuery = cart.useCartSuspenseQuery();
  const formState = useFormState(useMemo(() => ({
    remarks: cartQuery.data?.remarks ?? '',
    hasCoordination: cartQuery.data?.hasCoordination ?? false,
    serviceIds: cartQuery.data?.meta.serviceIds ?? [],
    catering: cartQuery.data?.meta.catering ?? CheckoutCateringFields.defaultValues().catering
  }), [cartQuery.data]), {
    remarks: {
      validate: value => z.string().optional().safeParse(value).success,
      message: i18n._('Remarks should be a string')
    }
  });
  const validateWithFeedback = useValidateWithFeedback(formState);
  const servicesQuery = api.useGraphqlQuery(QUERIES.services);
  const updateCheckoutExtraMutation = api.useGraphqlMutation(UPDATE_CHECKOUT_MUTATION, {
    throwOnError: false,
    onSuccess() {
      router.push(PORTAL_RELATIVE_HREF_CHECKOUT('account'));
    },
    onError(error) {
      notifications.dispatchGraphqlError(error);
      formState.updateErrors(processGraphqlFormErrors(error));
    }
  });
  if (!cartQuery.data) redirect(router.relativePath(PORTAL_RELATIVE_HREF_CART));
  return <CheckoutPageTemplate previousButton={{
    href: router.relativePath(PORTAL_RELATIVE_HREF_CHECKOUT('info'))
  }} nextButton={{
    label: i18n._('checkout.cta.to-account', undefined, {
      message: 'Fill in your details'
    }),
    async onClick() {
      if (!validateWithFeedback()) return;
      if (!cart) return;
      await updateCheckoutExtraMutation.mutateAsync({
        cartId: cartQuery.data!.id.toString(),
        input: formState.values
      });
    }
  }}>
            <CheckoutExtraPageForm formState={formState} services={servicesQuery.data?.activityServices} isFetchingServices={servicesQuery.isLoading} participants={cartQuery.data.participants} />
        </CheckoutPageTemplate>;
}
const QUERIES = {
  services: graphql(`
        query ActivityServices {
            activityServices {
                id
                name
                description
                priceMin
                priceMax
            }
        }
    `)
};
const UPDATE_CHECKOUT_MUTATION = graphql(`
    mutation updateCheckoutExtra($cartId: ID!, $input: UpdateCheckoutExtraInput!) {
        updateCheckoutExtra(cartId: $cartId, input: $input) {
            cart {
                ...DefaultCart
            }
        }
    }
`);